<template>
  <div class="glass p-8 h-full flex flex-col">
    <!-- Signature mark: the same 3-stop scale used across every choropleth map -->
    <div class="flex items-center gap-2 mb-3">
      <span
        class="w-8 h-1.5 rounded-full bg-gradient-to-r from-[#14b8a6] via-[#fbbf24] to-[#f43f5e]"
        aria-hidden="true"
      ></span>
      <span class="text-xs uppercase tracking-wider text-ink-300"
        >Nuevo en la plataforma</span
      >
    </div>

    <h2 class="text-xl font-display font-semibold text-ink-900 mb-1">Solicita tu acceso</h2>
    <p class="text-ink-400 text-sm mb-6">
      Cuéntanos sobre tu equipo y un asesor configurará tu cuenta y tu
      región de trabajo.
    </p>

    <!-- Success state -->
    <div v-if="submitted" class="flex-1 flex flex-col justify-center">
      <div
        class="p-4 rounded-xl bg-emerald-50 border border-emerald-200"
      >
        <div class="flex items-start gap-3">
          <svg
            class="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p class="text-emerald-700 text-sm">
            Listo. Revisamos tu solicitud y te contactaremos en
            <span class="text-ink-900 font-medium">{{ email }}</span> en breve.
          </p>
        </div>
      </div>

      <button
        type="button"
        @click="resetForm"
        class="text-ink-400 hover:text-violet-600 text-sm transition-colors mt-4 self-start"
      >
        Enviar otra solicitud
      </button>
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="submit" class="space-y-4 flex-1 flex flex-col">
      <!-- Honeypot: hidden from real visitors, catches basic bots -->
      <input
        type="text"
        v-model="honeypot"
        name="company_website"
        autocomplete="off"
        tabindex="-1"
        aria-hidden="true"
        class="absolute -left-[9999px] w-px h-px opacity-0"
      />

      <div>
        <label class="block text-sm text-ink-500 mb-2"
          >Nombre completo</label
        >
        <input
          type="text"
          v-model="name"
          class="input-field"
          placeholder="Tu nombre"
          required
        />
      </div>

      <div>
        <label class="block text-sm text-ink-500 mb-2"
          >Correo electrónico</label
        >
        <input
          type="email"
          v-model="email"
          class="input-field"
          placeholder="tu@email.com"
          required
        />
      </div>

      <div>
        <label class="block text-sm text-ink-500 mb-2">Teléfono</label>
        <input
          type="tel"
          v-model="phone"
          class="input-field"
          placeholder="10 dígitos"
        />
      </div>

      <div>
        <label class="block text-sm text-ink-500 mb-2"
          >Qué estás buscando</label
        >
        <textarea
          v-model="message"
          rows="3"
          class="input-field resize-none"
          placeholder="Ej. monitoreo electoral en 3 municipios, análisis de AGEB para la campaña..."
        ></textarea>
      </div>

      <button
        type="submit"
        class="btn-primary w-full flex items-center justify-center gap-2 mt-auto"
        :disabled="loading"
      >
        <svg
          v-if="loading"
          class="w-5 h-5 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <span>Registrar interés</span>
      </button>
    </form>
  </div>
</template>

<script>
import { defineComponent, ref } from "vue";
import { serverTimestamp } from "firebase/firestore";
import { useDBStore } from "@/stores/db";
import { useNotification } from "@/composables/useNotification";

export default defineComponent({
  name: "InterestForm",
  setup() {
    const dbStore = useDBStore();
    const notify = useNotification();

    const name = ref("");
    const email = ref("");
    const phone = ref("");
    const organization = ref("");
    const message = ref("");
    const honeypot = ref("");

    const loading = ref(false);
    const submitted = ref(false);

    const resetForm = () => {
      name.value = "";
      email.value = "";
      phone.value = "";
      message.value = "";
      honeypot.value = "";
      submitted.value = false;
    };

    const submit = async () => {
      if (!name.value.trim() || !email.value.trim()) {
        notify.error("Completa tu nombre y correo electrónico");
        return;
      }

      // Silently "succeed" for bots without writing to Firestore
      if (honeypot.value) {
        submitted.value = true;
        return;
      }

      loading.value = true;

      try {
        await dbStore.addDocument("leads", {
          name: name.value.trim(),
          email: email.value.trim(),
          phone: phone.value.trim() || null,
          organization: organization.value.trim() || null,
          message: message.value.trim() || null,
          status: "new",
          source: "login_page",
          createdAt: serverTimestamp(),
        });

        submitted.value = true;
        notify.success("Solicitud enviada");
      } catch (error) {
        notify.error("No se pudo enviar la solicitud: " + error.message);
      } finally {
        loading.value = false;
      }
    };

    return {
      name,
      email,
      phone,
      organization,
      message,
      honeypot,
      loading,
      submitted,
      submit,
      resetForm,
    };
  },
});
</script>
